import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import type { DimensionDisplayData } from './dimensionHelpers';

interface FingerprintRingsProps {
  dims: DimensionDisplayData[];
  size?: number;
  visible: boolean;
}

// Total entrance animation duration, and per-ring draw duration. Rings are
// staggered so all N rings finish across the full total duration.
const ANIMATION_TOTAL = 3;   // seconds — user requested 3s end-to-end
const RING_DURATION = 0.6;   // seconds — individual ring draw

export const FingerprintRings = React.memo(function FingerprintRings({
  dims,
  size = 130,
  visible,
}: FingerprintRingsProps) {
  // Bump on page load + tab visibility-regain → re-mount the rings so the
  // framer-motion initial → animate transition replays.
  const [replayKey, setReplayKey] = useState(0);
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        setReplayKey((k) => k + 1);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const cx = size / 2;
  const cy = size / 2;
  const maxR = size / 2 - 8;
  const minR = 14;

  // Only unlocked dims draw a ring. Ring-to-dim mapping is 1:1 in order,
  // so the inner ring corresponds to the first diagnostic bar below.
  const rings = dims.filter((d) => !d.locked);
  const N = rings.length;
  const stagger = N > 1 ? (ANIMATION_TOTAL - RING_DURATION) / (N - 1) : 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ opacity: visible ? 1 : 0, transition: 'opacity 300ms ease-out' }}
    >
      <defs>
        <filter id="fp-ring-glow">
          <feGaussianBlur stdDeviation="2.5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <radialGradient id="fp-center-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#F0A500" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#F0A500" stopOpacity="0" />
        </radialGradient>
      </defs>

      <circle cx={cx} cy={cy} r={maxR * 0.25} fill="url(#fp-center-glow)" />

      {rings.map((dim, i) => {
        // Radius increases linearly from inner to outer.
        const r = N === 1 ? (minR + maxR) / 2 : minR + ((maxR - minR) * i) / (N - 1);
        const arcFraction = Math.min(1, Math.max(0, dim.value / 100));
        const circumference = 2 * Math.PI * r;
        const drawn = arcFraction * circumference;
        const isHigh = dim.value >= 80;

        // Using <circle> + stroke-dasharray is the cleanest way to render
        // arbitrary fractions of a full circle (SVG <path> arcs can't
        // express a single 360° arc). Dash offset animates the draw-in.
        return (
          <motion.circle
            key={`${dim.key}-${replayKey}`}
            cx={cx}
            cy={cy}
            r={r}
            stroke={dim.color}
            strokeWidth={isHigh ? 2.5 : 1.8}
            strokeLinecap="round"
            fill="none"
            transform={`rotate(-90 ${cx} ${cy})`}
            strokeDasharray={`${drawn} ${circumference}`}
            filter={isHigh ? 'url(#fp-ring-glow)' : undefined}
            initial={{ strokeDashoffset: drawn, opacity: 0 }}
            animate={{ strokeDashoffset: 0, opacity: 0.88 }}
            transition={{
              duration: RING_DURATION,
              delay: i * stagger,
              ease: [0.16, 1, 0.3, 1],
            }}
          />
        );
      })}

      <circle cx={cx} cy={cy} r={3.5} fill="#F0A500" filter="url(#fp-ring-glow)" />
    </svg>
  );
});
